import React from "react";
import { render, unmountComponentAtNode } from "react-dom";
import { Action } from "redux";
import { Task } from "redux-saga";
import { call, select, takeEvery } from "redux-saga/effects";

import { getCustomization } from "@multiweb/customization";
import {
    ActionArea,
    createInitFrameWithDataSourceAction,
    DefaultRowTemplate,
    InitFrameConfig,
    LiteListFrame,
} from "@multiweb/frames";
import { ISectionLogger } from "@multiweb/logging";
import { HasMultiselect } from "@multiweb/reactConnectors/mixins/hasMultiselect";
import { bridge } from "@multiweb/reactCore/bridge";
import {
    createIsAtLeastOneRecordChecked,
    createUpdateRecordActionsSuccessAction,
    ICheckAllRecordsAction,
    IDataSourceAttribute,
    IDataSourceConditions,
    IDataSourceInitConfig,
    IRefreshDataSuccessAction,
    ISetDatasourceMultiselectModeAction,
    IToggleCheckOnSelectedRecordsAction,
    IToggleSelectedRecordCheckAction,
    IUncheckAllRecordsAction,
} from "@multiweb/reactCore/datasource";
import { createRootComponent, StoreManager } from "@multiweb/reactSetup";
import { EMPTY_OBJECT } from "@multiweb/reactUtils";
import { CaseInsensitiveMap, OrderType } from "@multiweb/types/multiweb";
import { OpenEditWindowParams, SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import { DataConverter } from "Global/Base/Converters/DataConverter";
import { IImplicitFormInfo } from "OW.Remoting/Remoting/OWManagerInterfaces";
import { IActionContext } from "UI/UI.Controls/Actions/IAction";
import { IFRMParams } from "UI/UI/UI/Controls/FormParameters";
import { FormManager } from "UI/UI/UI/FormManager/IFormManager";
import { FrameSharedMethods } from "UI/UI/UI/XGControls/Frames/FrameSharedMethods";
import { ModalWindowType } from "UI/UI/UI/XGControls/ModalWindowInterfaces";
import { AppContext } from "../../../App";
import { ControlWithLoggerParams } from "../../UI/UI/Controls/ControlInterfaces";
import { GeneralFrameConnector } from "../GeneralFrameConnector";
import { HasAutorefresh } from "../mixins/hasAutorefresh";
import { HasFulltextSearch } from "../mixins/hasFulltextsearch";
import { HasMasterFrame } from "../mixins/hasMasterFrame";
import { ILiteListFrameConnector } from "../types";

const { dispatchAction, runSaga, stringToEnumValue, isWFDefinedToClass } = bridge;
type SetAtLeastOneRecordChecked = (value: boolean) => void;
type IsAtLeastOneRecordCheckedSelector = ReturnType<typeof createIsAtLeastOneRecordChecked>;

/**
 * Slouží k propojení stávající aplikace a LiteListFrame.
 */
export class LiteListFrameConnector extends HasMultiselect(HasMasterFrame(HasFulltextSearch(HasAutorefresh(GeneralFrameConnector)))) implements ILiteListFrameConnector {
    private readonly _logger: ISectionLogger;
    private readonly _sagaTask: Task;

    constructor({ logger, ...otherParams }: ControlWithLoggerParams) {
        super(otherParams);
        this._logger = logger;
        this.hideButtons = FrameSharedMethods.parseHideButtons(
            this.ownerForm,
            this.element.getAttribute("data-hidebuttons"),
            this.isMainFrame,
        );
        this._sagaTask = runSaga(
            liteListFrameConnectorSaga,
            this.element.getAttribute("id"),
            (value) => this.setAtLeastOneRecordChecked(value),
            createIsAtLeastOneRecordChecked(),
        );
    }

    public override async initializeVisual(): Promise<void> {
        const developmentMode = !!AppContext.instance.configuration.developmentMode;
        const RootComponent = createRootComponent(
            developmentMode,
        );

        const datasourceKey: string = this.element.getAttribute("id");
        const attributes = await this._getAttributes();
        const dataSourceConfig: IDataSourceInitConfig = {
            objectName: this.objectName,
            attributes,
            formContext: this.ownerForm.formContext ?? EMPTY_OBJECT,
            recordIdKey: "id",
            hasWorkflow: this.hideButtons.workflow ? false : await isWFDefinedToClass(this.objectName),
            isPersistent: true,
        };

        const orderBy = FrameSharedMethods.parseOrderBy(this.element.getAttribute("data-orderby"), [{ attributeName: "ID", order: OrderType.ascending }]);
        const conditions: Partial<IDataSourceConditions> = {
            condition: this.element.getAttribute("data-condition"),
        };

        this.loadOnScroll = DataConverter.toBoolean(this.element.getAttribute("data-loadonscroll"), true);
        const rowTemplate = this.element.getAttribute("data-rowtemplate") || DefaultRowTemplate.singleLine;
        const actionArea = stringToEnumValue(ActionArea, this.element.getAttribute("data-actionarea"));
        const showHeader = DataConverter.toBoolean(this.element.getAttribute("data-showheader"), true);
        const hasDetailForm = !!this.detailFormName;

        const frameConfig: InitFrameConfig = {
            initialActionArea: actionArea ?? undefined,
            rowTemplate,
            initialShowHeader: showHeader,
            initialLoadOnScroll: this.loadOnScroll,
            emptyMessageCustomization: FrameSharedMethods.parseEmptyMessageCustomization(this.element),
            allowMultiselect: this.allowMultiSelect,
        };

        StoreManager.initializeStore(developmentMode);
        dispatchAction(
            createInitFrameWithDataSourceAction(
                this.element.id,
                this.canValueLoad,
                !this.ownerForm.readOnly,
                dataSourceConfig,
                hasDetailForm,
                frameConfig,
                orderBy,
                conditions,
            ),
        );
        await super.initializeVisual();
        dispatchAction(createUpdateRecordActionsSuccessAction(this.element.id, this.getReactRowActions()));

        render(
            <RootComponent>
                <LiteListFrame
                    dataSourceKey={datasourceKey}
                />
            </RootComponent>,
            this.reactContainer,
        );
    }

    private _getAttributes(): IDataSourceAttribute[] {
        const attributesElements = this.element.querySelectorAll<HTMLElement>("[data-behaviour='column']");
        const backgroundAttributes: CaseInsensitiveMap<IDataSourceAttribute> = new CaseInsensitiveMap();
        const result: IDataSourceAttribute[] = [];

        attributesElements.forEach(element => {
            const attributeRenderer = getCustomization().liteListFrame.attribute.findRenderer(element);
            if (attributeRenderer) {
                result.push(...attributeRenderer.getAttributes(element));
            } else {
                this._logger.warn(
                    `'data-tscitem = ${element.getAttribute(
                        "data-tscitem",
                    )}' is not supported in LiteListFrame`,
                );
            }
        });

        if (result.length === 0) {
            this._logger.warn("No attributes to fetch from server");
        }

        if (result.length > 0) {
            result.forEach((attribute) => {
                const { backgroundAttrName } = attribute;
                // Pokud existuje background atribut, tak se ověří, že nebyl už předtím uložen do resultu - nemělo by smysl ho znovu ukládat.
                if (
                    backgroundAttrName &&
                    !result.find((att) => att.attributeName === backgroundAttrName) &&
                    !backgroundAttributes.has(backgroundAttrName)
                ) {
                    backgroundAttributes.set(backgroundAttrName, {
                        attributeName: backgroundAttrName,
                        hidden: true,
                        isIcon: null,
                        iconName:null,
                        locKey: null,
                        displayFormat: null,
                        backgroundAttrName: null,
                        attributeRendererKey: null,
                        extendedProperties: EMPTY_OBJECT,
                    });
                }
            });
            result.push(...backgroundAttributes.values());
        }

        return [...result.values()];
    }

    private setAtLeastOneRecordChecked(value: boolean): void {
        this.atLeastOneRowChecked = value;
    }

    protected override _getOpenModeAttribute(): void {
        this.openMode = FrameSharedMethods.getOpenModeTypeFromAttribute(this.element);
    }

    /*
     * Zobrazení informací o atributu daného záznamu. Nic neprovede, pokud není uvedeno 'editWindow' nebo detailObject neobsahuje id pro LOVAtribut.
     */
    public async openAttributeEditWindow(openEditWindowParams: OpenEditWindowParams): Promise<void> {
        const { editWindow, recordId, attributeId } = openEditWindowParams;
        if (!editWindow) {
            this._logger.warn("Frame can not open attribute editWindow form without 'editWindow'.");
            return;
        }
        if (!recordId || !attributeId) {
            this._logger.warn("Frame can not open attribute editWindow without recordId or attributeId.");
            return;
        }
        const context: IActionContext = {
            ids: [recordId],
            className: this.objectName,
        };
        const formInfo: IImplicitFormInfo = await FrameSharedMethods.getDefaultFormName(
            editWindow,
            this.ownerForm,
            context,
            this.frameContext,
            this.masterAttr,
        );

        const params: IFRMParams = {
            ownerForm: this.ownerForm,
            hideWindowActions: true,
            viewMode: false,
            condition: `ID = ${attributeId}`,
            isImplicit: FrameSharedMethods.isFormNameImplicit(editWindow),
            formContext: this.ownerForm.formContext,
        };
        FormManager.instance.openFormModal({ formName: formInfo.FormName, parametricData: params, type: ModalWindowType.parametricForm });
    }

    public override dispose(): void {
        if (this.reactContainer)
            unmountComponentAtNode(this.reactContainer);
        super.dispose();
        this._sagaTask.cancel();
    }
}

/**
 * Hlavní sága pro LiteListFrameConnector. Poslouchá na relevantní akce z reduxu a zařizuje jejich obsluhu.
 */
const liteListFrameConnectorSaga: SagaWithReturnType<
    [string, SetAtLeastOneRecordChecked, IsAtLeastOneRecordCheckedSelector]
> = function* liteListFrameConnectorSaga(dataSourceKey, setAtLeastOneRecordChecked, isAtLeastOneRecordCheckedSelector) {
    yield takeEvery(
        (action: Action) =>
            (action instanceof IToggleCheckOnSelectedRecordsAction ||
            action instanceof ICheckAllRecordsAction ||
            action instanceof IUncheckAllRecordsAction ||
            action instanceof ISetDatasourceMultiselectModeAction ||
            action instanceof IToggleSelectedRecordCheckAction ||
            action instanceof IRefreshDataSuccessAction) &&
            action.meta.dataSourceKey === dataSourceKey,
        updateToolbarActionsEnabledVisualSaga,
        dataSourceKey,
        setAtLeastOneRecordChecked,
        isAtLeastOneRecordCheckedSelector,
    );
};

/**
 * Reaguje na změnu počtu označených řádků a podle toho aktualizuje viditelnost těch akcí v toolbaru, které se vztahují k záznamu.
 * Zajišťuje hlavně to, že pokud není vybrán žádný záznam, akce nebudou interactive (budou zašedlé) a nepůjde na ně kliknout.
 */
const updateToolbarActionsEnabledVisualSaga: SagaWithReturnType<
    [string, SetAtLeastOneRecordChecked, IsAtLeastOneRecordCheckedSelector]
> = function* updateToolbarActionsEnabledVisualSaga(dataSourceKey, setAtLeastOneRecordChecked, isAtLeastOneRecordCheckedSelector) {
    const isAtLeastOneRecordChecked: SagaYieldReturn<typeof isAtLeastOneRecordCheckedSelector> = yield select(
        isAtLeastOneRecordCheckedSelector,
        dataSourceKey,
    );

    yield call(setAtLeastOneRecordChecked, isAtLeastOneRecordChecked);
};
